import React, { useEffect } from 'react';
import { Typography, Box, Button, Alert } from '@mui/material';
import { Payment as PaymentIcon } from '@mui/icons-material';
import { useLiquidacion } from '../hooks/useLiquidacion';
import { useEmpresa } from '../context/EmpresaContext';
import ResumenCards from '../components/liquidacion/ResumenCards';
import TablaTrabajadoresPendientes from '../components/liquidacion/TablaTrabajadoresPendientes';
import TablaPagosPendientes from '../components/liquidacion/TablaPagosPendientes';
import DialogGenerarLiquidacion from '../components/liquidacion/DialogGenerarLiquidacion';
import DialogPagoDetalle from '../components/liquidacion/DialogPagoDetalle';
import DialogNuevoEgreso from '../components/liquidacion/DialogNuevoEgreso';

/**
 * Componente principal del módulo de Liquidación (VERSIÓN MODULAR)
 * Orquesta todos los componentes y la lógica de liquidación
 */
const LiquidacionAdmin = () => {
  const { simboloMoneda } = useEmpresa();
  const {
    // Estados
    trabajadoresPendientes,
    pagosPendientes,
    resumen,
    serviciosDetalle,
    egresosDetalle,
    selectedTrabajador,
    selectedPago,
    loading,
    error,
    success,
    dialogos,
    liquidacionForm,
    egresoForm,
    pagoForm,

    // Setters
    setError,
    setSuccess,
    setDialogos,
    setLiquidacionForm,
    setEgresoForm,
    setPagoForm,

    // Funciones de datos
    fetchTrabajadoresPendientes,
    fetchPagosPendientes,
    fetchResumen,

    // Funciones de liquidación
    abrirLiquidacion,
    generarLiquidacion,
    registrarEgreso,

    // Funciones de pagos
    abrirPagoDetalle,
    cerrarModalPago,
    actualizarEstadoPago
  } = useLiquidacion();

  // Cargar datos al montar el componente
  useEffect(() => {
    fetchTrabajadoresPendientes();
    fetchPagosPendientes();
    fetchResumen();
  }, [fetchTrabajadoresPendientes, fetchPagosPendientes, fetchResumen]);

  return (
    <Box sx={{ width: '100%', minHeight: '100%', p: { xs: 2, sm: 3, md: 4 } }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 3
      }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1A237E">
            Liquidaciones
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Comisiones, liquidaciones y pagos a trabajadores
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PaymentIcon />}
          onClick={() => setDialogos({ ...dialogos, nuevoEgreso: true })}
          sx={{
            background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 'bold',
            px: 3,
            '&:hover': { background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)' }
          }}
        >
          Nuevo Egreso
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Resumen Cards */}
      <ResumenCards resumen={resumen} />

      {/* Tabla de Trabajadores Pendientes */}
      <TablaTrabajadoresPendientes
        trabajadoresPendientes={trabajadoresPendientes}
        onLiquidar={abrirLiquidacion}
      />

      {/* Tabla de Pagos Pendientes */}
      <TablaPagosPendientes
        pagosPendientes={pagosPendientes}
        onVerDetalle={abrirPagoDetalle}
      />

      {/* Dialog: Generar Liquidación */}
      <DialogGenerarLiquidacion
        open={dialogos.liquidacion}
        onClose={() => setDialogos({ ...dialogos, liquidacion: false })}
        trabajador={selectedTrabajador}
        serviciosDetalle={serviciosDetalle}
        egresosDetalle={egresosDetalle}
        liquidacionForm={liquidacionForm}
        setLiquidacionForm={setLiquidacionForm}
        onGenerar={generarLiquidacion}
        loading={loading}
      />

      {/* Dialog: Detalle de Pago */}
      <DialogPagoDetalle
        open={dialogos.pagoDetalle}
        onClose={cerrarModalPago}
        pago={selectedPago}
        pagoForm={pagoForm}
        setPagoForm={setPagoForm}
        onActualizar={actualizarEstadoPago}
        loading={loading}
      />

      {/* Dialog: Nuevo Egreso */}
      <DialogNuevoEgreso
        open={dialogos.nuevoEgreso}
        onClose={() => setDialogos({ ...dialogos, nuevoEgreso: false })}
        trabajadoresPendientes={trabajadoresPendientes}
        egresoForm={egresoForm}
        setEgresoForm={setEgresoForm}
        onRegistrar={registrarEgreso}
        loading={loading}
        simboloMoneda={simboloMoneda}
      />
    </Box>
  );
};

export default LiquidacionAdmin;
